// public/sw.js mirrors this value because the worker is served as a standalone
// file. PWA foundation tests fail if the two contracts diverge.
export const PWA_WORKER_CONTRACT_VERSION = "legacy-recovery-v1";
export const PWA_WORKER_STATUS_MESSAGE = "YB_WORKER_STATUS";
export const PWA_RECOVERY_NAVIGATION_QUERY_KEY = "yb_pwa_recovery";
export const PWA_STARTUP_STATE_EVENT = "yb:pwa-startup-state";
export const PWA_STARTUP_DIAGNOSTICS_VERSION = 1;
export const PWA_STARTUP_DIAGNOSTICS_KEY = "yb:pwa-startup:v1";
export const PWA_STARTUP_MAX_EVENTS = 80;
export const PWA_DEPLOYMENT_RECOVERY_QUERY_KEY = "yb_pwa_chunk_recovery";
export const PWA_DEPLOYMENT_MANUAL_QUERY_KEY = "yb_pwa_manual_recovery";
export const PWA_DEPLOYMENT_RECOVERY_STORAGE_KEY = "yb:pwa-deployment-recovery:v1";
export const PWA_AUTOMATIC_RECOVERY_LATCH_STORAGE_KEY =
  "yb:pwa-automatic-recovery:v1";
export const PWA_DEPLOYMENT_RECOVERY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const PWA_STARTUP_ROUTE_RULES = {
  authCallbackPaths: [
    "/api/auth/callback",
    "/auth/callback",
    "/auth/confirm",
    "/auth/confirm-start",
    "/auth/handoff",
    "/auth/update-password",
    "/business-auth/post-confirm",
    "/oauth/callback",
    "/set-password",
  ],
  authCallbackQueryKeys: [
    "access_token",
    "code",
    "refresh_token",
    "token_hash",
  ],
  businessFormPatternSources: [
    "^/onboarding(?:/|$)",
    "^/business/listings/new(?:/|$)",
    "^/business/listings/[^/]+/edit(?:/|$)",
    "^/business-auth/create-password(?:/|$)",
    "^/business-auth/register(?:/|$)",
    "^/business/onboarding(?:/|$)",
    "^/business/profile(?:/|$)",
    "^/business/settings(?:/|$)",
    "^/business/staff/profile(?:/|$)",
  ],
};

function normalizedPathname(pathname) {
  const value = String(pathname || "/").trim() || "/";
  return value.length > 1 ? value.replace(/\/+$/, "") : value;
}

function hasQueryKey(search, keys) {
  if (!search) return false;
  try {
    const params = new URLSearchParams(search);
    return keys.some((key) => params.has(key));
  } catch {
    return true;
  }
}

export function getPwaPathCategory(pathname, search = "", hash = "") {
  const path = normalizedPathname(pathname);

  if (
    PWA_STARTUP_ROUTE_RULES.authCallbackPaths.some(
      (candidate) => path === candidate || path.startsWith(`${candidate}/`)
    )
  ) {
    return "auth_callback";
  }
  if (
    path === "/" &&
    (hasQueryKey(search, PWA_STARTUP_ROUTE_RULES.authCallbackQueryKeys) ||
      hasQueryKey(
        String(hash || "").replace(/^#/, ""),
        PWA_STARTUP_ROUTE_RULES.authCallbackQueryKeys
      ))
  ) {
    return "auth_callback";
  }
  if (path === "/business/pos" || path.startsWith("/business/pos/")) {
    return "pos";
  }
  if (path === "/checkout" || path.startsWith("/checkout/") || path === "/cart") {
    return "checkout";
  }
  if (
    path === "/business/dashboard" &&
    hasQueryKey(search, ["billing", "checkout", "stripe"])
  ) {
    return "payment_callback";
  }
  if (
    path.startsWith("/orders/") ||
    path === "/business/orders" ||
    path === "/account/orders" ||
    path === "/account/purchase-history"
  ) {
    return "receipt";
  }
  if (
    PWA_STARTUP_ROUTE_RULES.businessFormPatternSources.some((source) =>
      new RegExp(source).test(path)
    )
  ) {
    return "business_form";
  }
  if (path === "/" && !search && !hash) {
    return "startup";
  }
  if (path === "/") {
    return search ? "root_query" : "root_fragment";
  }
  return "general";
}

export function getPwaStaticReloadSafety(pathname, search = "", hash = "") {
  const category = getPwaPathCategory(pathname, search, hash);
  const reasons = {
    auth_callback: "Finish authentication before updating the app.",
    business_form: "Finish or leave the current business form before updating the app.",
    checkout: "Finish checkout before updating the app.",
    payment_callback: "Finish confirming the payment result before updating the app.",
    pos: "Finish or hold the active register work before updating the app.",
    receipt: "Finish the receipt or correction workflow before updating the app.",
  };
  const reason = reasons[category] || null;

  return {
    autoReload: category === "startup",
    category,
    reason,
    safe: !reason,
  };
}

export function getPwaWorkerUrl(version, { authDiagnostics = false } = {}) {
  const params = new URLSearchParams({
    c: PWA_WORKER_CONTRACT_VERSION,
    v: String(version || "development"),
  });
  if (authDiagnostics) params.set("auth_diag", "1");
  return `/sw.js?${params.toString()}`;
}

export function getPwaWorkerMetadata(scriptURL, origin = "https://yourbarrio.com") {
  if (!scriptURL) {
    return {
      contract: null,
      present: false,
      scriptPath: null,
      version: null,
    };
  }
  try {
    const url = new URL(scriptURL, origin);
    return {
      contract: url.searchParams.get("c"),
      present: true,
      scriptPath: url.pathname,
      version: url.searchParams.get("v"),
    };
  } catch {
    return {
      contract: null,
      present: true,
      scriptPath: null,
      version: null,
    };
  }
}

export function isExpectedPwaWorkerScript(scriptURL, expectedVersion, origin) {
  const metadata = getPwaWorkerMetadata(scriptURL, origin);
  return (
    metadata.present &&
    metadata.scriptPath === "/sw.js" &&
    metadata.contract === PWA_WORKER_CONTRACT_VERSION &&
    metadata.version === String(expectedVersion)
  );
}
